import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Paper,
  Chip,
  InputAdornment,
} from "@mui/material";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { AlertDialog } from "../../../components/AlertDialog";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import { ButtonStyled, useStyles } from "./styles";
import { Autocomplete } from "../../../components/Autocomplete";
import { Table as CustomTable } from "../../../components/Table";
import {
  ContactUpdate,
  FormikContact,
} from "../../../components/ContactUpdate";
import { ContactsActivate } from "../../../components/ContactsActivate";
import {
  getContacts,
  getInstances,
  putUpdateContact,
  postContact,
  instanceActivateContacts,
  desactiveContact,
} from "../../../services/whatsapp";
import {
  Contact,
  Instance,
  postContactObject,
  putUpdateContactObject,
  postInstaceActivateContacsObject,
  desactiveContactObject,
} from "../../../types/whatsapp";
import { useToast } from "../../../hooks/useToast";
import { useDialogConfirmation } from "../../../hooks/useDialogConfirmation";
import asyncForEach from "../../../utils/asyncForEach";
import DialogProgress from "../DialogProgress";
import { PhoneTwo } from "../../../utils/masks";

interface optionsFilterStatus {
  value: number;
  label: string;
}

const OPTIONSFILTERSTATUS: optionsFilterStatus[] = [
  { value: 1, label: "ativo" },
  { value: 0, label: "inativo" },
];

type ContactsProps = {
  instanceId?: number;
};

export function Contacts({ instanceId }: ContactsProps) {
  const queryClient = useQueryClient();
  const styles = useStyles();
  const toast = useToast();
  const dialog = useDialogConfirmation();

  const [instanceSelect, setInstanceSelect] = useState<Instance | null>(null);
  const [searchNameOrNumber, setSearchNameOrNumber] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<optionsFilterStatus | null>(
    null
  );

  const [contactOnDialog, setContactOnDialog] = useState<Contact | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<
    putUpdateContactObject[]
  >([]);
  const [contactsUpdated, setContactsUpdated] = useState<number>(0);
  const [dialogProgress, setDialogProgress] = useState<boolean>(false);
  const [openContactUpdate, setOpenContactUpdate] = useState<boolean>(false);
  const [openDialogActivateContacts, setOpenDialogActivateContacts] =
    useState<boolean>(false);

  const [openDialogDeactivateContact, setOpenDialogDeactivateContact] =
    useState<boolean>(false);

  const handleDialogDeactivateContact = (action = false, contact?: Contact) => {
    contact && setContactOnDialog(contact);
    setOpenDialogDeactivateContact(action);
  };

  const handleDialogActivateContacts = (action = false) => {
    setOpenDialogActivateContacts(action);
  };

  const handleDialogUpdatedContact = (
    action = false,
    contact: Contact | null = null
  ) => {
    setContactOnDialog(contact);
    setOpenContactUpdate(action);
  };

  const dataDeactivateContact = useMutation(
    async (query: desactiveContactObject) => {
      toast.show("Desativando o contato", { severity: "info" });
      return desactiveContact(query.numero, query.instance_id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          "contacts",
          instanceSelect?.id,
          filterStatus?.value,
          searchNameOrNumber,
        ]);

        toast.show("Contato desativado com sucesso", { severity: "success" });
      },
      onError: () => {
        toast.show("Erro ao desativar o contato", { severity: "error" });
      },
    }
  );

  const dataPostActivateInstanceContacts = useMutation(
    async (queryPost: postInstaceActivateContacsObject) => {
      toast.show("Ativando os contatos", { severity: "info" });
      return instanceActivateContacts(queryPost);
    },
    {
      onSuccess: async () => {
        toast.show("Contatos ativados com sucesso", { severity: "success" });
      },
      onError: () => {
        toast.show("Erro ao ativar os contatos", { severity: "error" });
      },
    }
  );

  const dataPostUpdateContact = useMutation(
    async (queryPut: postContactObject) => {
      toast.show("Criando o contato", { severity: "info" });
      return postContact(queryPut);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          "contacts",
          instanceSelect?.id,
          filterStatus?.value,
          searchNameOrNumber,
        ]);
        toast.show("Contato criado com sucesso!", { severity: "success" });
      },
      onError: () => {
        toast.show(
          "Erro ao cadastrar contato. Confira se contato já é cadastrado.",
          { severity: "error" }
        );
      },
    }
  );

  const dataPutUpdateContact = useMutation(
    async (queryPut: putUpdateContactObject) => {
      toast.show("Atualizando o contato", { severity: "info" });
      return putUpdateContact(queryPut);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          "contacts",
          instanceSelect?.id,
          filterStatus?.value,
          searchNameOrNumber,
        ]);
        toast.show("Contato atualizado com sucesso!!!", {
          severity: "success",
        });
      },
      onError: () => {
        toast.show("Erro ao atualizar o contato!!!", { severity: "error" });
      },
    }
  );

  const bulkUpdateContacts = useMutation(putUpdateContact);

  const instances = useQuery("instances", () => getInstances(), {
    cacheTime: 0,
    onSuccess: (response) => {
      if (Array.isArray(response)) {
        console.log(response[0]);
        setInstanceSelect(response[0]);
      }
    },
  });
  const contactsGeneral = useQuery(
    ["contacts", instanceSelect?.id],
    () => getContacts(undefined, instanceSelect?.id),
    {
      enabled: Boolean(instanceSelect?.id),
    }
  );

  const contacts = useMemo(() => {
    const regexIsNumber = /^[0-9]+$/;
    const name = !regexIsNumber.test(searchNameOrNumber)
      ? searchNameOrNumber
      : "";
    const cellphone = regexIsNumber.test(searchNameOrNumber)
      ? searchNameOrNumber
      : "";
    const instanceId = instanceSelect?.id;
    const status = filterStatus?.value;
    const contacts = [];
    const objFind = {
      ...(name.length && { nome_contato: name }),
      ...(cellphone.length && { numero: cellphone }),
      ...(typeof status === "number" && { ativo_envio: status }),
      ...(typeof instanceId === "number" && { id_instancia: instanceId }),
    };
    const keys = Object.keys(objFind) as [
      "id_instancia" | "ativo_envio" | "numero" | "nome_contato"
    ];

    if (!keys.length && contactsGeneral.data?.length) {
      contacts.push(...contactsGeneral.data);
    } else if (keys.length && contactsGeneral.data?.length) {
      const filterContacts = contactsGeneral.data?.filter((contact) => {
        let isReturn = false;
        for (let index = 0; index < keys.length; index += 1) {
          //'id_instancia' | 'ativo_envio'
          if (
            typeof contact[keys[index]] === "number" &&
            Number(contact[keys[index]]) === Number(objFind[keys[index]])
          ) {
            isReturn = true;
          } else if (
            typeof contact[keys[index]] === "string" &&
            contact[keys[index]]
              ?.toString()
              .toLowerCase()
              .includes(objFind[keys[index]]!?.toString().toLowerCase())
          ) {
            isReturn = true;
          } else {
            isReturn = false;
            break;
          }
        }
        return isReturn;
      });
      contacts.push(...filterContacts);
    }

    return { data: contacts };
  }, [
    instanceSelect?.id,
    filterStatus?.value,
    searchNameOrNumber,
    contactsGeneral,
  ]);

  const handleCreateOrUpdateContact = (contact: FormikContact) => {
    if (contact.id) {
      dataPutUpdateContact.mutateAsync({
        active: contact.status.value,
        cellphone: contact.numero,
        instance_id: contact.instance?.id!,
        contact_id: contact.id,
        name: contact.nome_contato,
      });
    } else {
      dataPostUpdateContact.mutateAsync({
        active: contact.status.value,
        cellphone: contact.numero,
        instance_id: contact.instance?.id!,
        name: contact.nome_contato,
      });
    }
  };

  const handleSelectedContacts = (contacts: Contact[]) => {
    const data = contacts.map((contact) => ({
      cellphone: contact.numero,
      instance_id: contact.id_instancia,
      contact_id: contact.id,
      name: contact.nome_contato as string,
      birth_date: contact.data_nascimento as string,
      document: contact.documento as string,
      genre: contact.sexo as string,
    }));
    setSelectedContacts(data);
  };

  const isLoading = useMemo(() => {
    return (
      instances.isLoading ||
      contactsGeneral.isLoading ||
      dataPutUpdateContact.isLoading ||
      dataPostUpdateContact.isLoading ||
      dataPostActivateInstanceContacts.isLoading
    );
  }, [
    instances.isLoading,
    contactsGeneral.isLoading,
    dataPutUpdateContact.isLoading,
    dataPostUpdateContact.isLoading,
    dataPostActivateInstanceContacts.isLoading,
  ]);

  const handleUpdateStatusContacts = useCallback(
    async (active: boolean) => {
      try {
        setDialogProgress(true);
        await asyncForEach(selectedContacts, async (row) => {
          await bulkUpdateContacts.mutateAsync({ ...row, active });
          setContactsUpdated((prev) => prev + 1);
        });
        await queryClient.invalidateQueries([
          "contacts",
          instanceSelect?.id,
          filterStatus?.value,
          searchNameOrNumber,
        ]);
        toast.show("Contatos atualizado com sucesso!!!", {
          severity: "success",
        });
      } catch (err) {
        toast.show("Erro ao atualizar os contatos!!!", { severity: "error" });
      } finally {
        setDialogProgress(false);
        setSelectedContacts([]);
        setContactsUpdated(0);
      }
    },
    [
      toast,
      searchNameOrNumber,
      filterStatus,
      instanceSelect,
      queryClient,
      bulkUpdateContacts,
      selectedContacts,
    ]
  );

  const handleActivateContacts = useCallback(() => {
    dialog.show("Ativar contatos", "Deseja ativar os contatos selecionados?", {
      onConfirmate: () => {
        dialog.close();
        handleUpdateStatusContacts(true);
      },
    });
  }, [dialog, handleUpdateStatusContacts]);

  const handleDeactivateContacts = useCallback(() => {
    dialog.show(
      "Desativar contatos",
      "Deseja desativar os contatos selecionados?",
      {
        onConfirmate: () => {
          dialog.close();
          handleUpdateStatusContacts(false);
        },
      }
    );
  }, [dialog, handleUpdateStatusContacts]);

  const actives = useMemo(() => {
    return contacts.data?.filter((data) => Boolean(data.ativo_envio)).length;
  }, [contacts.data]);

  const deactives = useMemo(() => {
    return contacts.data?.filter((data) => !Boolean(data.ativo_envio)).length;
  }, [contacts.data]);

  useEffect(() => {
    if (instanceId && instances.data) {
      const instance = instances.data.find(
        (instance) => instance.id === instanceId
      );
      if (instance) {
        setInstanceSelect(instance);
      }
    }
  }, [instanceId, instances.data]);

  return (
    <Box>
      <Grid
        px={"1.9rem"}
        display={"flex"}
        alignItems={"baseline"}
        justifyContent={"space-between"}
      >
        <Grid className={styles.gridFilters}>
          <TextField
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            label={"Pesquisar por nome ou número"}
            type={"text"}
            name={"searchContactNumber"}
            value={searchNameOrNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchNameOrNumber(e.target.value);
            }}
            sx={{
              "&.MuiTextField-root": {
                width: "19.69rem",
              },
            }}
          />

          <Autocomplete
            size="small"
            label={"Pesquisar por instância"}
            clearOnBlur={false}
            options={instances.data}
            getOptionLabel={(option) => option.nome}
            value={instanceSelect}
            onChange={(_, option) => {
              setInstanceSelect(option);
            }}
            sx={{ width: "20.69rem" }}
          />

          <Autocomplete
            size="small"
            label={"Filtrar por status"}
            clearOnBlur={false}
            options={OPTIONSFILTERSTATUS}
            getOptionLabel={(option) => option.label}
            value={filterStatus}
            onChange={(_, option) => {
              setFilterStatus(option);
            }}
            sx={{ width: "15.37rem" }}
          />
        </Grid>

        <Grid className={styles.gridButtons}>
          <ButtonStyled
            variant="outlined"
            className={styles.buttomContacts}
            onClick={() => {
              handleDialogUpdatedContact(true);
            }}
          >
            Adicionar contato
          </ButtonStyled>

          <ButtonStyled
            variant="outlined"
            className={styles.buttomContacts}
            onClick={() => {
              handleDialogActivateContacts(true);
            }}
          >
            Sincronizar contatos
          </ButtonStyled>
        </Grid>
      </Grid>

      <Grid mt={"2.5rem"} container height={"8.875rem"}>
        <Grid item xs={4} className={styles.gridContactsInfo}>
          <Paper className={styles.paperContactsInfo}>
            <Typography className={styles.typographyBigNumber}>
              {isLoading ? (
                <CircularProgress variant="indeterminate" />
              ) : (
                contacts.data?.length
              )}
            </Typography>
            <Typography className={styles.typographyTitle}>Contatos</Typography>
            <Typography className={styles.typographySubTitle}>
              Total de contatos
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4} className={styles.gridContactsInfo}>
          <Paper className={styles.paperContactsInfo}>
            <Typography className={styles.typographyBigNumber}>
              {isLoading ? (
                <CircularProgress variant="indeterminate" />
              ) : (
                actives
              )}
            </Typography>
            <Typography className={styles.typographyTitle}>Ativos</Typography>
            <Typography className={styles.typographySubTitle}>
              Total de contatos ativos
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4} className={styles.gridContactsInfo}>
          <Paper className={styles.paperContactsInfo}>
            <Typography className={styles.typographyBigNumber}>
              {isLoading ? (
                <CircularProgress variant="indeterminate" />
              ) : (
                deactives
              )}
            </Typography>
            <Typography className={styles.typographyTitle}>Inativos</Typography>
            <Typography className={styles.typographySubTitle}>
              Total de contatos inativos
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      <Collapse in={Boolean(selectedContacts.length)} unmountOnExit>
        <Stack
          spacing={2}
          direction="row"
          bgcolor="#F3F4FB"
          px={2}
          py={2}
          borderBottom="1px solid rgba(224, 224, 224, 1)"
          alignItems="center"
        >
          <Typography>
            {selectedContacts.length}{" "}
            {`contato${selectedContacts.length > 1 ? "s" : ""}`}{" "}
            {`selecionado${selectedContacts.length > 1 ? "s" : ""}`}
          </Typography>
          <Button
            onClick={handleActivateContacts}
            size="small"
            variant="outlined"
          >
            Ativar contatos
          </Button>
          <Button
            onClick={handleDeactivateContacts}
            variant="outlined"
            size="small"
          >
            Desativar contatos
          </Button>
        </Stack>
      </Collapse>
      <CustomTable
        toolbar={false}
        pageSize={10}
        data={contacts.data || []}
        isLoading={isLoading}
        headerStyle={{
          backgroundColor: "#F3F4FB",
          padding: "10px 0px",
        }}
        selection
        onSelectionChange={handleSelectedContacts}
        columns={[
          {
            field: "instancia",
            title: "Instância",
            align: "center",
            render: (rowData: Contact) => (
              <Typography className={styles.typographyCellTable}>
                {rowData.nome_instancia}
              </Typography>
            ),
          },
          {
            field: "status",
            title: "Status",
            align: "center",
            render: (rowData: Contact) => (
              <Chip
                label={rowData.ativo_envio ? "Ativo" : "Inativo"}
                size="small"
                sx={{
                  padding: "0px 0.5rem",
                  gap: "0.625rem",
                  borderRadius: "0.25rem",
                  lineHeight: "22px",
                  letterSpacing: "0.05rem",
                  fontSize: "12px",
                  background: rowData.ativo_envio ? "#52C41A" : "#B02F2F",
                  "& .MuiChip-label": {
                    padding: 0,
                    color: "#FFF",
                  },
                }}
              />
            ),
          },
          {
            field: "numero",
            title: "Número",
            align: "center",
            render: (rowData: Contact) => (
              <Typography className={styles.typographyCellTable}>
                {PhoneTwo(rowData.numero)}
              </Typography>
            ),
          },
          {
            field: "nome",
            title: "Nome",
            align: "center",
            render: (rowData: Contact) => (
              <Typography className={styles.typographyCellTable}>
                {rowData.nome_contato}
              </Typography>
            ),
          },
          {
            field: "nome",
            title: "",
            align: "right",
            render: (rowData: Contact) => (
              <Stack direction="row-reverse" spacing={1}>
                <Tooltip title="Editar" placement="top">
                  <IconButton
                    onClick={() => {
                      handleDialogUpdatedContact(true, rowData);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
        ]}
        localization={{
          header: {
            actions: "",
          },
          pagination: {
            labelRowsSelect: "linhas",
            labelDisplayedRows: "{count} de {from}-{to}",
            firstTooltip: "Primeira página",
            previousTooltip: "Página anterior",
            nextTooltip: "Próxima página",
            lastTooltip: "Última página",
          },
        }}
      />

      {openContactUpdate && (
        <ContactUpdate
          open={openContactUpdate}
          contact={contactOnDialog}
          loadingAutocomplete={false}
          instances={instances.data!}
          onClose={() => handleDialogUpdatedContact()}
          onSave={(contact: FormikContact) => {
            handleDialogUpdatedContact();
            handleCreateOrUpdateContact(contact);
          }}
        />
      )}

      {openDialogActivateContacts && (
        <ContactsActivate
          open={openDialogActivateContacts}
          instances={instances.data!}
          onClose={() => handleDialogActivateContacts()}
          onSave={(idInstance: number) => {
            dataPostActivateInstanceContacts.mutateAsync({
              client: idInstance,
              batch: false,
            });
            handleDialogActivateContacts();
          }}
        />
      )}
      {openDialogDeactivateContact && (
        <AlertDialog
          open={openDialogDeactivateContact}
          contentText={
            "Ao desativar um contato, você estará desativando o envio de mensagens para esse número."
          }
          title={"Deseja desativar este contato?"}
          textAgree={"Desativar"}
          handleAgree={() => {
            handleDialogDeactivateContact();
            contactOnDialog &&
              dataDeactivateContact.mutateAsync({
                instance_id: contactOnDialog.id_instancia!,
                numero: contactOnDialog.numero!,
              });
          }}
          handleClose={() => handleDialogDeactivateContact()}
        />
      )}

      <DialogProgress
        open={dialogProgress}
        total={selectedContacts.length}
        progress={contactsUpdated}
      />
    </Box>
  );
}
